/*
  Plays the social grid's reels only while they are on screen.

  Four looping videos streaming at once is a lot to ask of a phone, so each
  card starts when it scrolls into view and pauses when it leaves. Nothing
  here is required for the section to work: without JavaScript, and under
  reduced motion, every card stays a still with its play badge.
*/

class EthirajReels extends HTMLElement {
  connectedCallback() {
    if (this.dataset.autoplay === 'false') return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.reduceMotion.matches) return;

    this.videos = Array.from(this.querySelectorAll('video'));
    if (!this.videos.length || !('IntersectionObserver' in window)) return;

    // A card is "on screen" once a third of it is visible — enough that a
    // card clipped at the edge of the viewport does not start playing.
    this.observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) this.start(entry.target);
      else this.stop(entry.target);
    }), { threshold: 0.34 });

    // The badge follows the element's own events rather than the play()
    // promise: a video that stalls and recovers, or that a browser pauses on
    // its own, then still reports the truth.
    this.videos.forEach((video) => {
      video.addEventListener('playing', () => this.mark(video, true));
      video.addEventListener('pause', () => this.mark(video, false));
      video.addEventListener('error', () => this.mark(video, false));
      this.observer.observe(video);
    });

    // Playing to a hidden tab wastes the visitor's battery and data.
    this.onVisibility = () => {
      if (document.hidden) this.videos.forEach((video) => this.stop(video));
      else this.videos.forEach((video) => { if (video.dataset.inView === 'true') this.start(video); });
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    // Someone can turn reduced motion on mid-visit.
    this.onMotionChange = (event) => {
      if (!event.matches) return;
      this.videos.forEach((video) => this.stop(video));
    };
    this.reduceMotion.addEventListener('change', this.onMotionChange);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onMotionChange) this.reduceMotion.removeEventListener('change', this.onMotionChange);
  }

  start(video) {
    video.dataset.inView = 'true';
    if (this.reduceMotion.matches || document.hidden) return;

    // preload is "none" in the markup so an off-screen card costs nothing.
    if (video.preload === 'none') video.preload = 'auto';

    const played = video.play();
    // A browser may refuse — a data-saver mode, or a codec it will not take.
    // The still underneath is already the right thing to show, so there is
    // nothing to do but swallow the rejection; the 'playing' event, not this
    // promise, is what reveals the video.
    if (played && typeof played.catch === 'function') played.catch(() => {});
  }

  stop(video) {
    video.dataset.inView = 'false';
    if (!video.paused) video.pause();
    this.mark(video, false);
  }

  mark(video, playing) {
    const card = video.closest('.ehf-social__card');
    if (card) card.classList.toggle('is-playing', playing);
  }
}

if (!customElements.get('ethiraj-reels')) {
  customElements.define('ethiraj-reels', EthirajReels);
}
